// Status and action names are owned by the yaml at
// near-miss-backend/src/auth/indorama.yaml (workflow `ess_suggestion`)
// and surfaced through /auth/workflow/ess_suggestion. The frontend treats
// them as opaque strings — every label, transition, action button and
// short label is read from that endpoint at runtime.
export type SuggestionStatus = string;
export type SuggestionAction = string;

export const CATEGORIES = [
  "Productivity",
  "Cost Reduction",
  "OHS & Environment",
  "Quality Improvement",
  "Work Process Simplification",
  "Systems, Processes & Practices",
  "Customer Satisfaction",
  "Safety Improvement",
  "Environmental",
  "Health",
  "System improvement",
  "Cost saving",
  "GHG reduction",
  "System / work simplification",
  "Others",
] as const;

export type Category = (typeof CATEGORIES)[number];

// Picking this category unlocks a free-text "Specify category" input. The
// text is persisted server-side in metadata.other_category and surfaced
// as `other_category` on the row; `category` itself stays "Others" so
// filters / stats keep a stable key.
export const OTHER_CATEGORY = "Others";

export const isOtherCategory = (category: string | null | undefined): boolean =>
  (category ?? "").trim().toLowerCase() === OTHER_CATEGORY.toLowerCase();

// Human label for a row's category: "Others: <text>" when the submitter
// specified one, otherwise the category as stored.
export const displayCategory = (
  s: Pick<Suggestion, "category" | "other_category">,
): string => {
  const text = (s.other_category ?? "").trim();
  return isOtherCategory(s.category) && text
    ? `${s.category}: ${text}`
    : s.category;
};

// One reviewer slot assigned to an ESS suggestion. Mirrors the backend
// AssignedReviewer schema (stored inside metadata.assigned_reviewers).
// `user_name` is hydrated server-side so the UI can render without a
// follow-up lookup.
export interface AssignedReviewer {
  user_id: string;
  user_name: string | null;
  committee: "wc" | "sc";
  sub_role: "chairman" | "facilitator" | "member" | "convener";
  // The committee slot the entry fills, as keyed by the workflow config's
  // `committees` block. Absent on rows assigned before slots existed.
  slot?: string;
  assigned_by: string;
  assigned_at: string;
}

// One slot in an ESS reviewer group. Same triple the
// `assign_reviewers` action takes, so the autofill flow can replay each
// entry through the existing mutation without translation.
export interface ReviewerGroupMember {
  user_id: string;
  committee: "wc" | "sc";
  sub_role: "chairman" | "facilitator" | "member";
}

export interface ReviewerGroup {
  id: string;
  account_id: string;
  category: string;
  members: ReviewerGroupMember[];
  created_by: string;
  created_at: string;
  updated_at: string;
}

// One note appended to a suggestion. Stored inside
// `metadata.notes` server-side and surfaced as a top-level `notes`
// array (mirrors how assigned_reviewers is surfaced). `created_by_name`
// is denormalized server-side so the UI renders without a lookup.
export interface SuggestionNote {
  id: string;
  note: string;
  attachment_ids: string[] | null;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
}

export interface Suggestion {
  id: string;
  suggestion_number: string | null;
  employee_name: string;
  employee_id: string;
  department: string;
  title: string;
  description: string;
  category: string;
  // Free text captured when category is "Others" (see OTHER_CATEGORY).
  other_category?: string | null;
  submitted_on_behalf_of: string | null;
  attachment_ids: string[] | null;
  status: SuggestionStatus;
  status_label: string | null;
  remarks: string | null;
  account_id: string;
  submitter_user_id: string;
  created_at: string;
  updated_at: string;
  // Generic structured-data bag on the row. First consumer is
  // `assigned_reviewers` below; future ESS-only structured fields will
  // land here without another migration. Backend schema defaults this to
  // `{}` (never null) — see near-miss-backend src/schemas/ess.py — but the
  // `| null` is kept defensively since some legacy rows/paths may omit it.
  metadata?: Record<string, unknown> | null;
  assigned_reviewers?: AssignedReviewer[];
  notes?: SuggestionNote[];
}

export interface SuggestionCreateInput {
  title: string;
  description: string;
  category: string;
  // Required by the backend (400) when category is "Others"; ignored otherwise.
  other_category?: string;
  department: string;
  employee_name: string;
  employee_id: string;
  submitted_on_behalf_of?: string;
  // The account behind the on-behalf name, when the submitter picked the
  // beneficiary out of the user search rather than just typing a name.
  // Backend stores it alongside the free-text name (max 255).
  submitted_on_behalf_of_user_id?: string;
  attachment_ids?: string[];
}

// Mirrors the backend EditSuggestionRequest. All fields are optional —
// only fields the HR reviewer actually edited are sent so the audit
// trail records exactly what changed. Status / role / department gating
// is enforced server-side by the yaml `edit_suggestion` predicate.
export interface EditSuggestionInput {
  title?: string;
  description?: string;
  category?: string;
  // Only meaningful alongside (or on a row already at) category "Others";
  // switching category away from "Others" clears it server-side.
  other_category?: string;
  department?: string;
  employee_name?: string;
  employee_id?: string;
  attachment_ids?: string[];
  remarks?: string;
}

// Body for POST /ess/{id}/notes. `attachment_ids` are asset ids
// returned by uploadAsset. Auth (which stage / role can add a note) is
// enforced server-side by the yaml `add_note` predicate.
export interface CreateNoteInput {
  note: string;
  attachment_ids?: string[];
}

export interface SuggestionListResponse {
  suggestions: Suggestion[];
  total: number;
}

export interface SuggestionHistoryItem {
  id: number;
  from_status: SuggestionStatus | null;
  to_status: SuggestionStatus;
  remarks: string | null;
  updated_by: string | null;
  updated_by_name: string | null;
  created_at: string;
}

export interface SuggestionStats {
  total: number;
  by_status: Record<string, number>;
  by_category: Record<string, number>;
  by_department: Record<string, number>;
}

export interface SuggestionListFilters {
  status?: SuggestionStatus[];
  category?: string[];
  department?: string[];
  // Unit isn't a column on the row (only `department` is) — resolved via
  // `resolveSuggestionUnit` below, same client-side post-filter mechanism
  // as status/category/department (see api/ess.ts `listSuggestions`).
  unit?: string[];
  // A–F band computed by the SC evaluation (`rewardCategoryFor` below).
  // Resolved via `suggestionEvaluations(row).ssc?.reward_category`, same
  // client-side post-filter mechanism.
  reward_category?: string[];
  date_from?: string;
  date_to?: string;
  my_suggestions?: boolean;
}

export interface AssetUploadResponse {
  asset_id: string;
}

export interface PresignedUrlResponse {
  asset_id: string;
  url: string;
}

// --- Evaluation (WC/SC scoring) ---------------------------------------
// Criteria + bands are procedure-mandated (Annexure-I / Annexure-II).
// Max values and band boundaries must not be adjusted without a change
// to the source procedure document.

export const WC_CRITERIA = [
  {
    key: "business_gain",
    label: "Business Gain",
    max: 40,
    bands: [
      { v: 10, hint: "≤ $10k/yr" },
      { v: 20, hint: "$10–25k" },
      { v: 30, hint: "$25–50k" },
      { v: 40, hint: "> $50k" },
    ],
  },
  {
    key: "adaptability_practicability",
    label: "Adaptability & Practicability",
    max: 30,
    bands: [
      { v: 5, hint: "Single area, needs revision" },
      { v: 10, hint: "2–3 sections" },
      { v: 15, hint: "Own division" },
      { v: 20, hint: "Other divisions" },
    ],
  },
  {
    key: "quality_improvement",
    label: "Quality Improvement",
    max: 10,
    bands: [
      { v: 2, hint: "≤ 1%" },
      { v: 4, hint: "1–2%" },
      { v: 6, hint: "2–3%" },
      { v: 10, hint: "> 3%" },
    ],
  },
  {
    key: "efforts",
    label: "Efforts",
    max: 20,
    bands: [
      { v: 8, hint: "Basic / routine" },
      { v: 14, hint: "Good novelty" },
      { v: 20, hint: "Creative / innovative" },
    ],
  },
] as const; // Annexure-I

export const SSC_CRITERIA = [
  {
    key: "business_gain",
    label: "Business Gain",
    max: 40,
    bands: [
      { v: 10, hint: "≤ $10k/yr" },
      { v: 20, hint: "$10–25k" },
      { v: 30, hint: "$25–50k" },
      { v: 40, hint: "> $50k" },
    ], // same as WC
  },
  {
    key: "adaptability_practicability",
    label: "Adaptability & Practicability",
    max: 20,
    bands: [
      { v: 10, hint: "Single area" },
      { v: 15, hint: "2–3 sections" },
      { v: 20, hint: "Own division" },
    ],
  },
  {
    key: "quality_improvement",
    label: "Quality Improvement",
    max: 10,
    bands: [
      { v: 2, hint: "≤ 1%" },
      { v: 4, hint: "1–2%" },
      { v: 6, hint: "2–3%" },
      { v: 10, hint: "> 3%" },
    ], // same as WC
  },
  {
    key: "scope_other_areas",
    label: "Usable in Other Areas",
    max: 10,
    bands: [
      { v: 5, hint: "Good" },
      { v: 7, hint: "Very good" },
      { v: 10, hint: "Excellent" },
    ],
  },
  {
    key: "innovativeness",
    label: "Innovativeness",
    max: 10,
    bands: [
      { v: 5, hint: "Good" },
      { v: 7, hint: "Very good" },
      { v: 10, hint: "Excellent" },
    ],
  },
  {
    key: "roi_period",
    label: "ROI Period",
    max: 10,
    bands: [
      { v: 5, hint: "3 years" },
      { v: 7, hint: "2 years" },
      { v: 10, hint: "1 year" },
    ],
  },
] as const; // Annexure-II

export type WcOutcome = "in_process" | "recommend" | "not_recommend";
export type SscOutcome = "in_process" | "approved" | "rejected";

export interface EvaluationRecord {
  scores: Record<string, number>;
  total: number;
  outcome: string;
  comment?: string | null;
  attachment_ids?: string[] | null;
  member_scores?: {
    member_name: string;
    member_user_id?: string | null;
    scores: Record<string, number>;
  }[];
  meeting_date?: string | null;
  reward_category?: string | null;
  verifier_departments?: string[] | null;
  // Legacy single-department form of the field above, on SC records
  // written before verification could fan out to several departments.
  // Read on display only — never written.
  verifier_department?: string | null;
  evaluated_by?: string;
  evaluated_by_name?: string;
  evaluated_at?: string;
}

export const WC_PASS_MARK = 30,
  SSC_PASS_MARK = 40,
  SLA_DAYS = 90;

/**
 * The outcome a committee's total implies. The evaluator no longer picks
 * one — a score at or above the pass mark is a recommendation/approval and
 * anything below it is not, so a passing score can never be filed as a
 * rejection (or the reverse). `in_process` is no longer produced; legacy
 * records may still carry it, so display paths must tolerate it.
 */
export function deriveOutcome(
  committee: "wc" | "ssc",
  total: number,
): WcOutcome | SscOutcome {
  const passed = total >= (committee === "wc" ? WC_PASS_MARK : SSC_PASS_MARK);
  if (committee === "wc") return passed ? "recommend" : "not_recommend";
  return passed ? "approved" : "rejected";
}

// Reward bands `rewardCategoryFor` can return. Single source of truth for
// the Reward Category filter's option list (SuggestionFilters.tsx) — mirrors
// how CATEGORIES anchors the Category filter.
export const REWARD_CATEGORIES = ["A", "B", "C", "D", "E", "F"] as const;

export const rewardCategoryFor = (total: number): string =>
  total <= 50
    ? "F"
    : total <= 60
      ? "E"
      : total <= 70
        ? "D"
        : total <= 80
          ? "C"
          : total <= 90
            ? "B"
            : "A";

/**
 * The departments an SC evaluation nominated to verify the benefits.
 * Tolerates `verifier_department` — the single-valued field that records
 * written before verification could fan out still carry. Read on display
 * only; never written back in that form.
 */
export function verifierDepartments(rec?: EvaluationRecord): string[] {
  if (rec?.verifier_departments?.length) return rec.verifier_departments;
  return rec?.verifier_department ? [rec.verifier_department] : [];
}

// --- Implementation (per verifier department) ---------------------------
// Written by the backend `record_department_implementation` self-transition
// on `accepted_by_sc` into metadata.implementation.departments[<dept>].
// Once every nominated department is marked implemented the backend moves
// the suggestion to `implemented` on its own.

export interface ImplementationComment {
  text: string;
  by?: string | null;
  by_name?: string | null;
  at?: string | null;
}

export interface DepartmentImplementation {
  implemented: boolean;
  implemented_by?: string | null;
  implemented_by_name?: string | null;
  implemented_at?: string | null;
  comments?: ImplementationComment[] | null;
}

export interface SuggestionImplementation {
  departments?: Record<string, DepartmentImplementation> | null;
}

export const suggestionImplementation = (
  s: Suggestion,
): SuggestionImplementation =>
  ((s.metadata as any)?.implementation ?? {}) as SuggestionImplementation;

export const suggestionEvaluations = (
  s: Suggestion,
): { wc?: EvaluationRecord; ssc?: EvaluationRecord } =>
  ((s.metadata as any)?.evaluations ?? {}) as {
    wc?: EvaluationRecord;
    ssc?: EvaluationRecord;
  };

/**
 * Resolve a suggestion's unit for filtering/display. Prefers
 * `metadata.unit` (stamped on rows created after the unit field landed)
 * and falls back to a department -> unit lookup for legacy rows that
 * predate it. The lookup is supplied by the caller — built from the
 * `/departments/` catalog via `unitGroupLabel` (src/utils/departments.ts)
 * so common/unit-less departments resolve to "Common" — since a
 * `Suggestion` row only ever carries a department *name*, not its unit.
 */
export function resolveSuggestionUnit(
  s: Pick<Suggestion, "department" | "metadata">,
  unitByDepartment: Record<string, string>,
): string | undefined {
  const metaUnit = (s.metadata as Record<string, unknown> | null | undefined)
    ?.unit;
  if (typeof metaUnit === "string" && metaUnit) return metaUnit;
  return unitByDepartment[s.department];
}
